/**
 * v2 Help / FAQ — a single in-app reference for every workflow plus the
 * external API. Static content (no data fetching); kept here so it ships
 * and versions with the UI it documents.
 */
import { Link } from "react-router-dom";

import { Code, HelpTopic, K, Section } from "../help/components";
import {
  HELP_GROUP_ORDER,
  NAV_GROUP_LABEL,
  topicsInGroup,
} from "../help/registry";

const TOC: ReadonlyArray<{ id: string; label: string }> = [
  { id: "pages", label: "Pages reference" },
  { id: "overview", label: "Overview" },
  { id: "nav", label: "Getting around" },
  { id: "jobs", label: "Jobs & live updates" },
  { id: "inventory", label: "Inventory (PON / VDSL)" },
  { id: "provisioning", label: "Provisioning (Pending ONUs)" },
  { id: "maintenance", label: "Maintenance & destructive ops" },
  { id: "profiles", label: "Config profiles" },
  { id: "backups", label: "Backups & verify" },
  { id: "alarms", label: "Alarms" },
  { id: "schedules", label: "Schedules & bulk jobs" },
  { id: "templates", label: "Command templates" },
  { id: "lookups", label: "Lookups (ONU / MAC / PPPoE)" },
  { id: "vpn", label: "Off-net access (VPN)" },
  { id: "api", label: "External API" },
  { id: "faq", label: "FAQ / troubleshooting" },
];

export function Help() {
  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6 pb-16">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-semibold text-slate-100">Help &amp; FAQ</h1>
        <p className="text-sm text-slate-400">
          How the chassis tool works, end to end — and how to call the
          external API.
        </p>
      </header>

      {/* Table of contents */}
      <nav className="rounded-lg bg-slate-900 shadow-border p-3">
        <p className="mb-2 text-[10px] uppercase tracking-wide text-slate-500">
          On this page
        </p>
        <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs sm:grid-cols-3">
          {TOC.map((t) => (
            <li key={t.id}>
              <a
                href={`#${t.id}`}
                className="text-emerald-400 hover:text-emerald-300"
              >
                {t.label}
              </a>
            </li>
          ))}
        </ul>
      </nav>

      {/* ---------------- Page-by-page reference ---------------- */}
      <section id="pages" className="scroll-mt-20 flex flex-col gap-4">
        <div className="flex flex-col gap-1">
          <h2 className="border-b border-slate-800 pb-1 text-base font-semibold text-slate-100">
            Pages reference
          </h2>
          <p className="text-sm text-slate-400">
            Every page in the app, grouped by where it lives in the nav, with a
            breakdown of what each item does and a sample screenshot. Pages
            marked <strong>Admin only</strong> are restricted in the app, but
            everyone can read how they work here.
          </p>
        </div>
        {HELP_GROUP_ORDER.map((group) => (
          <div
            key={group}
            id={`pages-${group}`}
            className="scroll-mt-20 flex flex-col gap-3"
          >
            <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500">
              {NAV_GROUP_LABEL[group]}
            </h3>
            {topicsInGroup(group).map((t) => (
              <div
                key={t.id}
                id={t.id}
                className="scroll-mt-20 rounded-lg bg-slate-900 shadow-border p-3"
              >
                <HelpTopic entry={t} />
              </div>
            ))}
          </div>
        ))}
      </section>

      <Section id="overview" title="Overview">
        <p>
          This tool manages GPON and VDSL access chassis (OLTs) across your
          Gigafy locations: it inventories ONUs/ports, collects optical and
          line-rate health, provisions and tears down subscribers, takes and
          verifies config backups, and surfaces alarms. Every change is
          performed by a background <strong>job</strong> that SSHes (or, for
          some vendors, HTTPS) to the device, and every operator action is
          recorded in the <Link to="/settings/audit" className="text-emerald-400 hover:text-emerald-300">Audit log</Link>.
        </p>
        <p>
          You sign in with your Gigafy credentials; the tool acts on the
          chassis using each chassis's resolved SSH credentials.
        </p>
      </Section>

      <Section id="nav" title="Getting around">
        <ul className="list-disc pl-5">
          <li>
            <strong>Home</strong> — the fleet, grouped by location. Each card
            shows health pills (good / warning / critical), unacknowledged
            alarm badges, a <K>⚠ N pending</K> badge when a chassis has
            discovered-but-unprovisioned ONUs, and quick-job buttons.
          </li>
          <li>
            <strong>Chassis page</strong> — tabs: <em>Inventory</em>,{" "}
            <em>Backups</em>, <em>Alarms</em>, <em>Activity</em>,{" "}
            <em>Pending</em> (GPON), <em>Deployment</em> (GPON), and{" "}
            <em>Administration</em>.
          </li>
          <li>
            <strong>Tools ▾</strong> — ONU lookup, MAC map, Bulk jobs,
            Terminal sessions, Locks.
          </li>
          <li>
            <strong>Settings ▾</strong> — Jobs, Templates, Schedules, Audit
            log, Alarm rules, API keys (admin), My settings.
          </li>
        </ul>
      </Section>

      <Section id="jobs" title="Jobs & live updates">
        <p>
          Anything that touches a device is a job. Submit one from a chassis
          page or a Home quick-job button; it appears in the{" "}
          <strong>inline job tracker</strong> at the foot of the page and in{" "}
          <Link to="/settings/jobs" className="text-emerald-400 hover:text-emerald-300">Settings → Jobs</Link>. Click a job to see its
          per-target results and the live device transcript.
        </p>
        <p>
          The UI is event-driven: when a job changes inventory, alarms, or
          config, the relevant views refresh <strong>automatically</strong>{" "}
          (no manual reload). After a successful provision the tool also fires
          a confirming <K>list_onus</K> so the grid reflects the device's real
          post-commit state.
        </p>
      </Section>

      <Section id="inventory" title="Inventory (PON / VDSL)">
        <p>
          The Inventory tab shows the port grid. For GPON, each ONU row shows
          display ID, ONU id, serial, state, last optical RX power, and the
          last-seen MAC / device name. For VDSL, each port shows line state,
          actual up/down rates, and SNR margins. Click any cell to copy it;
          click the ⓘ to expand a row for MAC history + recent optical
          samples.
        </p>
        <p>
          Run <K>List ONUs</K> / <K>List ports</K> to refresh from the device,{" "}
          <K>Collect optical</K> / <K>Collect line speed</K> for health, and{" "}
          <K>Collect MAC table</K> to map subscriber MACs to ports.
        </p>
      </Section>

      <Section id="provisioning" title="Provisioning (Pending ONUs)">
        <p>
          A <strong>pending</strong> ONU is one the OLT has discovered on a PON
          but which isn't provisioned yet. Run <K>List pending ONUs</K>, then
          open the <strong>Pending</strong> tab:
        </p>
        <ol className="list-decimal pl-5">
          <li>Pick a PON port — its pending serials are listed.</li>
          <li>
            Set the services once (VNET / bandwidth / shaper / cosq). The
            dropdowns are populated from the chassis's parsed config profiles;
            you can also type a custom value. A profile not present on the OLT
            is flagged so you don't ship a typo.
          </li>
          <li>
            Display/ONU IDs are auto-picked gap-first; submit to provision the
            whole port. One job per ONU is queued.
          </li>
        </ol>
        <p>
          Submitted ONUs drop out of the Pending list immediately and their
          progress shows in the results panel; the inventory grid updates on
          completion. The tool won't double-provision: a serial already live,
          or one with a provision already in flight, is skipped with a reason.
        </p>
      </Section>

      <Section id="maintenance" title="Maintenance & destructive ops">
        <p>
          Under <strong>Deployment → Maintenance</strong> (GPON): Move ONU,
          Bulk PON clear, Replace VNET, Provision ONU, Deprovision ONU, Swap
          ONU. All are <strong>typed-confirm gated</strong> and run under a
          hard chassis lock. Port and Display ID fields are dropdowns sourced
          from inventory, so they match what the device stored.
        </p>
        <ul className="list-disc pl-5">
          <li>
            <strong>Bulk PON clear</strong> and <strong>Replace VNET</strong>{" "}
            use a <em>Preview</em> step that resolves the scope into the exact
            ONUs affected before you submit.
          </li>
          <li>
            <strong>Deprovision</strong> removes a single ONU (the OLT cascades
            its services + subscriber).
          </li>
        </ul>
      </Section>

      <Section id="profiles" title="Config profiles">
        <p>
          <strong>Deployment → Config profiles</strong> lists the bandwidth /
          shaper / vnet / cosq profiles parsed from the chassis's latest
          backup, with usage counts. Click <K>Re-parse</K> after taking a fresh
          backup to refresh them. These feed the provisioning dropdowns.
        </p>
      </Section>

      <Section id="backups" title="Backups & verify">
        <p>
          The Backups tab snapshots the running-config (
          <K>backup_running_config</K>) and keeps history. Profiles are parsed
          from the latest backup automatically. <strong>Verify config</strong>{" "}
          cross-checks ONUs vs subscriber blocks in the latest backup and lists
          any inconsistencies — no device access needed.
        </p>
      </Section>

      <Section id="alarms" title="Alarms">
        <p>
          <K>Fetch alarms</K> pulls active alarms into the Alarms tab and the
          navbar bell. Acknowledge or mute individual alarms.{" "}
          <Link to="/settings/alarm-rules" className="text-emerald-400 hover:text-emerald-300">Settings → Alarm rules</Link>{" "}
          maps text patterns to severities that drive the badge colours.
        </p>
      </Section>

      <Section id="schedules" title="Schedules & bulk jobs">
        <p>
          <Link to="/settings/schedules" className="text-emerald-400 hover:text-emerald-300">Settings → Schedules</Link>{" "}
          runs recurring jobs (cron / interval) — e.g. nightly backups or
          optical sweeps. <Link to="/tools/bulk-jobs" className="text-emerald-400 hover:text-emerald-300">Tools → Bulk jobs</Link>{" "}
          fans a single job out across many chassis at once and rolls the
          results up under one parent.
        </p>
      </Section>

      <Section id="templates" title="Command templates">
        <p>
          <Link to="/settings/templates" className="text-emerald-400 hover:text-emerald-300">Settings → Templates</Link>{" "}
          holds the per-vendor CLI templates the worker executes (provision,
          deprovision, move, replace-vnet, bulk-clear), with version history
          and rollback. Edits create a new active version; the shipped YAML is
          only the seeded baseline. Editing a template incorrectly (e.g.
          removing the <K>enable</K>/<K>configure</K> prelude) can break a job —
          roll back if a change misbehaves.
        </p>
      </Section>

      <Section id="lookups" title="Lookups (ONU / MAC / PPPoE)">
        <ul className="list-disc pl-5">
          <li>
            <strong>ONU lookup</strong> — find an ONU's history by serial
            across all chassis.
          </li>
          <li>
            <strong>MAC map</strong> — cross-chassis MAC → device-name lookup.
          </li>
          <li>
            <strong>Sync PPPoE (MikroTik)</strong> — pulls active PPPoE
            sessions from the location's core router and maps each session's
            MAC to its Gigafy device name. This is what lets a GIG-ID resolve
            to a serving ONU/port (used by the external API below).
          </li>
        </ul>
      </Section>

      <Section id="vpn" title="Off-net access (VPN)">
        <p>
          Some chassis live on networks the stack can't reach directly. An
          admin can configure an OpenVPN client under{" "}
          <Link to="/settings/vpn" className="text-emerald-400 hover:text-emerald-300">Settings → VPN</Link>: server
          address, protocol, username/password (write-only — stored encrypted,
          never shown again), a verify-cert toggle, and the{" "}
          <strong>off-net subnets</strong> to route through the tunnel. It's{" "}
          <strong>split-tunnel</strong> — only those subnets use the VPN; all
          other traffic is unaffected.
        </p>
        <p>
          Save, then <em>Connect</em>. A <K>VPN</K> indicator appears in the
          top bar while the tunnel is up/connecting, and connect/disconnect
          events are recorded in the <Link to="/settings/audit" className="text-emerald-400 hover:text-emerald-300">Audit log</Link>.
        </p>
      </Section>

      {/* ---------------- External API ---------------- */}
      <Section id="api" title="External API">
        <p>
          A JSON API for other systems (e.g. a management portal or an AI
          assistant) to look up subscribers, chassis port state, MAC
          sightings, jobs and audit — plus a single refresh trigger. It is
          authenticated by an <strong>API key</strong>, separate from operator
          login.
        </p>
        <p className="rounded border border-emerald-900/60 bg-emerald-950/20 p-2 text-xs">
          <strong>Full integration spec:</strong>{" "}
          <a
            href="/boxman-primer.md"
            download
            className="text-emerald-400 hover:text-emerald-300"
          >
            download boxman-primer.md
          </a>{" "}
          — a self-contained primer (auth, every endpoint, examples, error
          codes, rate limits) you can hand to a developer or feed to an AI
          assistant to build an integration.
        </p>

        <h3 className="mt-2 text-sm font-medium text-slate-100">
          1. Get a key (admin only)
        </h3>
        <p>
          Key management is restricted to the admin allowlist (
          <K>CHASSIS_API_KEY_ADMINS</K>). If you're an admin, open{" "}
          <Link to="/settings/api-keys" className="text-emerald-400 hover:text-emerald-300">Settings → API keys</Link>, click{" "}
          <em>Create key</em>, and <strong>copy the key immediately</strong> —
          it's shown once and only its hash is stored. Revoke a key any time;
          calls using it start failing at once.
        </p>

        <h3 className="mt-2 text-sm font-medium text-slate-100">
          2. Authenticate
        </h3>
        <p>
          Send the key as a bearer token on every request. No cookie/session is
          used for <K>/api/ext/*</K>.
        </p>
        <Code>{`Authorization: Bearer gk_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`}</Code>

        <h3 className="mt-2 text-sm font-medium text-slate-100">
          3. Look up a subscriber
        </h3>
        <Code>{`GET /api/ext/subscriber/{GIG_ID}

# example
curl -s \\
  -H "Authorization: Bearer gk_xxxxxxxx..." \\
  https://<your-host>/api/ext/subscriber/GIG-1234`}</Code>

        <p>GPON response:</p>
        <Code>{`{
  "gig_id": "GIG-1234",
  "found": true,
  "technology": "gpon",
  "chassis": { "id": 1, "name": "GIG-1044A", "host": "10.8.57.25" },
  "port": "1",
  "onu_id": "7",
  "display_id": "107",
  "serial": "RDSY940611A0",
  "state": "up",
  "mac": "aa:bb:cc:dd:ee:ff",
  "signal": {
    "rx_power_dbm": -22.4,
    "tx_power_dbm": 2.1,
    "observed_at": "2026-06-04T14:02:11Z"
  },
  "sync": null,
  "resolved_via": "pppoe",
  "note": null
}`}</Code>

        <p>VDSL response (signal is null; sync is populated):</p>
        <Code>{`{
  "gig_id": "GIG-5678",
  "found": true,
  "technology": "vdsl",
  "chassis": { "id": 4, "name": "GIG-1044F", "host": "10.8.57.40" },
  "port": "0/2/3",          // frame/slot/port
  "onu_id": null,
  "state": "activated",
  "mac": "11:22:33:44:55:66",
  "signal": null,
  "sync": {
    "down_kbps": 48000, "up_kbps": 12000,
    "down_snr_db": 8.5, "up_snr_db": 7.0,
    "observed_at": "2026-06-04T14:00:00Z"
  },
  "resolved_via": "pppoe"
}`}</Code>

        <p>Not yet resolvable:</p>
        <Code>{`{ "gig_id": "GIG-9999", "found": false,
  "note": "No MAC for this GIG-ID. It needs an active PPPoE session
           (mikrotik_pppoe_sync) or a portal MAC sync to be resolvable." }`}</Code>

        <h3 className="mt-2 text-sm font-medium text-slate-100">Field reference</h3>
        <ul className="list-disc pl-5">
          <li><K>found</K> — true when the GIG-ID resolved to a current port.</li>
          <li><K>technology</K> — <K>gpon</K> or <K>vdsl</K>.</li>
          <li><K>chassis</K> — serving chassis <K>id</K>, display <K>name</K>, and management <K>host</K> (IP).</li>
          <li><K>port</K> — PON port (GPON) or <K>frame/slot/port</K> (VDSL).</li>
          <li><K>onu_id</K> / <K>display_id</K> / <K>serial</K> — GPON ONU identity.</li>
          <li><K>signal</K> — GPON optical RX/TX power (dBm) + sample time.</li>
          <li><K>sync</K> — VDSL actual rates (kbps) + SNR margins (dB) + sample time.</li>
          <li><K>resolved_via</K> — how the MAC was found: <K>pppoe</K> (live session) &gt; <K>mikrotik</K> &gt; <K>portal</K> cache.</li>
        </ul>

        <h3 className="mt-2 text-sm font-medium text-slate-100">
          How resolution works (and freshness)
        </h3>
        <p>
          The lookup maps GIG-ID → MAC → port using the latest scans. For it to
          resolve, the serving chassis must have recent{" "}
          <K>mikrotik_pppoe_sync</K> (MAC ↔ GIG-ID) and <K>collect_mac</K> (MAC ↔
          ONU/port) data — schedule those if results are stale or empty. A
          known GIG-ID whose MAC isn't on any current port returns{" "}
          <K>found: false</K> with a <K>note</K> telling you to run{" "}
          <K>collect_mac</K>.
        </p>

        <h3 className="mt-2 text-sm font-medium text-slate-100">
          Chassis queries (diagnostics scope, v0.31.0)
        </h3>
        <p>
          These take the <strong>chassis's own device name</strong> (the OLT,
          e.g. <K>GIG-1044F</K>), not a subscriber GIG-ID:
        </p>
        <Code>{`# all ports + current known state (GPON ONUs / VDSL lines)
GET /api/ext/chassis/{GIG_ID}/ports

# one port
GET /api/ext/chassis/{GIG_ID}/ports/pon/{port}          # GPON
GET /api/ext/chassis/{GIG_ID}/ports/vdsl/{slot}/{port}  # VDSL (frame 0)

# force a state refresh (list_onus on GPON, collect_line_speed on VDSL),
# then poll the job and re-read the ports
POST /api/ext/chassis/{GIG_ID}/refresh
GET  /api/ext/jobs/{job_id}

# where has this MAC been seen (OLT / VDSL / PPPoE / portal, timestamped)
GET /api/ext/mac/{mac}`}</Code>
        <p>
          The refresh trigger is the only write on the key channel: it queues
          the same read-only collection job the UI's refresh button does,
          dedupes against an in-flight run (<K>deduped: true</K>), and
          enforces a per-chassis cooldown (default 5 min).
        </p>

        <h3 className="mt-2 text-sm font-medium text-slate-100">Errors</h3>
        <ul className="list-disc pl-5">
          <li><K>401 API_KEY_REQUIRED</K> — missing/!malformed Authorization header.</li>
          <li><K>401 API_KEY_INVALID</K> — unknown or revoked key.</li>
          <li><K>403 API_KEY_SCOPE_MISSING</K> — key lacks the endpoint's scope.</li>
          <li><K>403 IP_NOT_ALLOWED</K> — source IP outside the admin allowlist.</li>
          <li><K>404 CHASSIS_NOT_FOUND / PORT_NOT_FOUND</K> — wrong name, port, or technology path.</li>
          <li><K>422 INVALID_MAC</K> — MAC didn't parse.</li>
          <li><K>429 RATE_LIMITED / REFRESH_COOLDOWN</K> — honour <K>Retry-After</K>.</li>
          <li><K>200</K> with <K>found:false</K> — valid key, GIG-ID/MAC not (yet) resolvable.</li>
        </ul>
        <p className="text-xs text-slate-500">
          Limits: 60 requests/min per key, 120/min per IP (defaults), one
          refresh per chassis per 5 min. Data changes on the scan cadence —
          cache reads for at least a minute.
        </p>
      </Section>

      <Section id="faq" title="FAQ / troubleshooting">
        <ul className="list-disc pl-5">
          <li>
            <strong>A change didn't show up.</strong> The grid auto-refreshes on
            job completion; if a device is slow, give the confirming{" "}
            <K>list_onus</K> a moment. The Activity tab + Settings → Jobs show
            the truth.
          </li>
          <li>
            <strong>Profiles list looks short.</strong> Take a fresh backup,
            then <K>Re-parse</K> under Config profiles.
          </li>
          <li>
            <strong>A job failed.</strong> Open it (Settings → Jobs or the
            tracker) — the per-target results carry the device's error output.
          </li>
          <li>
            <strong>API lookup returns found:false.</strong> Run / schedule{" "}
            <K>Sync PPPoE</K> and <K>Collect MAC table</K> on the serving
            chassis.
          </li>
          <li>
            <strong>Can't see Settings → API keys.</strong> It's admin-only —
            ask an admin to add you to <K>CHASSIS_API_KEY_ADMINS</K>.
          </li>
        </ul>
      </Section>
    </div>
  );
}
